import { z } from 'zod';

// Contrato de la pantalla Capacitaciones (so=360) validado con Zod (ENG-VAL-01): un
// drift del backend cae al estado de error de la query, nunca a un throw en render.
// El avance es de LECTURA de la versión vigente (el curso vive en las competencias).

const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const DISPATCH_STATES = [
  'sent',
  'queued',
  'retrying',
  'failed',
  'unlinked',
  'version_gap',
  'retired',
  // G2-D: la aprobación de la versión se devolvió; la nueva firma la vuelve a disparar.
  'returned',
] as const;
export const READER_STATES = ['read', 'pending', 'overdue'] as const;
export const COMPETENCY_STATUSES = ['met', 'pending', 'expired'] as const;

export const summarySchema = z.object({
  documents: z.number(),
  assigned: z.number(),
  completed: z.number(),
  pending: z.number(),
  overdue: z.number(),
  coveragePct: z.number().nullable(),
});

export const trainingRowSchema = z.object({
  entryId: z.string(),
  code: z.string(),
  title: z.string().nullable(),
  versionNumber: z.number().nullable(),
  revisionLabel: z.string().nullable(),
  assigned: z.number(),
  completed: z.number(),
  pending: z.number(),
  overdue: z.number(),
  progressPct: z.number().nullable(),
  dueDate: date.nullable(),
  dispatch: z
    .object({ state: z.enum(DISPATCH_STATES), approvedVersion: z.number(), at: z.string() })
    .nullable(),
});

export const overviewResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    asOf: date,
    summary: summarySchema,
    total: z.number(),
    items: z.array(trainingRowSchema),
  }),
});

export const readerSchema = z.object({
  lmsUserId: z.number(),
  name: z.string(),
  positions: z.array(z.string()),
  state: z.enum(READER_STATES),
  readAt: z.string().nullable(),
  dueDate: date.nullable(),
});

export const readersResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    entry: z.object({ id: z.string(), code: z.string(), title: z.string().nullable() }),
    versionNumber: z.number().nullable(),
    revisionLabel: z.string().nullable(),
    truncated: z.boolean(),
    // Conteos exactos (con la lista truncada vienen del avance del LMS); null si no se conocen.
    totals: z.object({ assigned: z.number(), read: z.number(), overdue: z.number() }).nullable(),
    readers: z.array(readerSchema),
  }),
});

export const competencySchema = z.object({
  name: z.string(),
  category: z.string(),
  position: z.string(),
  required: z.boolean(),
  status: z.enum(COMPETENCY_STATUSES),
  metAt: z.string().nullable(),
});

export const competenciesResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    person: z.object({ lmsUserId: z.number(), name: z.string() }),
    competencies: z.array(competencySchema),
  }),
});

export type TrainingSummary = z.infer<typeof summarySchema>;
export type TrainingRow = z.infer<typeof trainingRowSchema>;
export type TrainingOverview = z.infer<typeof overviewResponseSchema>['data'];
export type Reader = z.infer<typeof readerSchema>;
export type DocumentReaders = z.infer<typeof readersResponseSchema>['data'];
export type Competency = z.infer<typeof competencySchema>;
export type ReaderCompetencies = z.infer<typeof competenciesResponseSchema>['data'];
